import { useState, useEffect, useCallback } from 'react'
import { Search, Plus, Puzzle } from 'lucide-react'

/**
 * Editor for `type: fragment-ref` fields — picks a reusable `builder_fragment`
 * to embed (via the Fragment block's subroot), or creates a new one.
 *
 * Value shape : `{nid, slug, label}` (the Fragment block fetches the full
 * fragment syncable by nid at render time).
 *
 * Existing fragments come from `/api/builder/search/fragments`. "Create" POSTs
 * a new builder_fragment (with a default, immediately-editable unit build) via
 * `/decoupled-api/save` — CSRF + credentials are injected by `fetchPatch.js`
 * for `/decoupled-api/*`. The new fragment is then selected, and the editor
 * edits it in place once embedded.
 */

// A minimal default build: a single editable heading, itself a unit (so the
// fragment is editable in place the moment it's embedded). Nested under
// `objectData.data` (the fragment's decoupled data nesting).
const DEFAULT_BUILD = `data: {}
renderView:
  - type: BuilderUnit
    dataBaseLocation: ~~.apiData.data.objectData.data
    content:
      - type: div
        attributes: { class: "p-6 text-center" }
        content:
          - type: h3
            editable: { field: heading, type: text }
            attributes: { class: "text-xl font-semibold text-gray-800" }
            content: ~~.apiData.data.objectData.data.heading
  - type: DataSync
    path: ~~.apiData
    mode: onIdle
    idleDelay: 1500
`

const slugify = (s) =>
  (s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 64)

export const FragmentRefEditor = ({ value, onSave, onClose }) => {
  const [q, setQ] = useState('')
  const [results, setResults] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [newName, setNewName] = useState('')
  const [creating, setCreating] = useState(false)

  const search = useCallback(async (term) => {
    setLoading(true)
    setError(null)
    try {
      const res = await fetch(`/api/builder/search/fragments?q=${encodeURIComponent(term)}&limit=50`, { credentials: 'include' })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      setResults(await res.json())
    } catch (e) {
      setError('Impossible de charger les fragments.')
      console.error('FragmentRefEditor search failed', e)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { search('') }, [search])

  const pick = (frag) => onSave({ nid: frag.nid, slug: frag.slug, label: frag.label })

  const createFragment = async () => {
    const name = newName.trim() || 'Nouveau fragment'
    const slug = `${slugify(name) || 'fragment'}-${Math.random().toString(36).slice(2, 7)}`
    setCreating(true)
    setError(null)
    try {
      // C1: a fragment is a builder_unit with an inline build (no template ref).
      const res = await fetch('/decoupled-api/save?display=decoupled&entity_type=node&bundle=builder_unit', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          data: {
            title: name,
            field_unit_slug: slug,
            field_unit_build: DEFAULT_BUILD,
            field_unit_data: JSON.stringify({ data: { heading: name } }),
          },
        }),
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const body = await res.json()
      const nid = body?.id ?? body?.data?.id
      if (!nid) throw new Error('No nid in save response')
      onSave({ nid: Number(nid), slug, label: name })
    } catch (e) {
      setError('Échec de la création du fragment.')
      console.error('FragmentRefEditor create failed', e)
    } finally {
      setCreating(false)
    }
  }

  const filtered = q.trim()
    ? results.filter(r => `${r.label} ${r.slug}`.toLowerCase().includes(q.trim().toLowerCase()))
    : results

  return (
    <div className="flex flex-col gap-4">
      {/* Search existing */}
      <div className="relative">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Rechercher un fragment…"
          className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500"
          autoFocus
        />
      </div>

      {error && <p className="text-xs text-red-600">{error}</p>}
      {loading && <p className="text-xs text-gray-400">Chargement…</p>}

      <ul className="max-h-64 overflow-auto divide-y divide-gray-100 border border-gray-100 rounded-lg">
        {filtered.length === 0 && !loading && (
          <li className="px-3 py-4 text-xs text-gray-400 text-center">Aucun fragment.</li>
        )}
        {filtered.map((frag) => {
          const selected = value?.nid === frag.nid
          return (
            <li key={frag.nid}>
              <button
                type="button"
                onClick={() => pick(frag)}
                className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-teal-50 transition-colors ${selected ? 'bg-teal-50' : ''}`}
              >
                <Puzzle size={14} className="text-teal-600 shrink-0" />
                <span className="font-medium text-gray-700 truncate">{frag.label}</span>
                <span className="text-xs text-gray-400 truncate ml-auto">{frag.slug}</span>
              </button>
            </li>
          )
        })}
      </ul>

      {/* Create new */}
      <div className="flex items-center gap-2 pt-1 border-t border-gray-100">
        <input
          type="text"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder="Nom d'un nouveau fragment…"
          className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500"
        />
        <button
          type="button"
          onClick={createFragment}
          disabled={creating}
          className="btn btn-primary"
        >
          <Plus size={15} /> {creating ? 'Création…' : 'Créer'}
        </button>
      </div>

      <div className="flex justify-end">
        <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700">
          Fermer
        </button>
      </div>
    </div>
  )
}
